import { Alert, Button } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IThirdPartyMessage } from './AdminProcessDetailsThirdPartyMessages';
import type { AdminProcess } from './adminProcessDetailsTypes';
import { ThirdPartyMessageType } from '../../../../common/enums/thirdPartyMessageType';
import { translateKey } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import {
    AdminProcessNullableFormattedDatetime,
    AdminTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Modal } from '../../elements/modals/Modal';
import { CheckboxInput } from '../../fields/Checkbox';

interface IAdminProcessDetailsResendMessagesModalProps {
    adminProcess: AdminProcess;
    onClose: () => void;
}

export function AdminProcessDetailsResendMessagesModal(props: IAdminProcessDetailsResendMessagesModalProps) {
    const abortableFetch = useAbortableFetch();
    const [thirdPartyMessages, setThirdPartyMessages] = useState<IThirdPartyMessage[]>([]);
    const [checkedThirdPartyMessages, setCheckedThirdPartyMessages] = useState<string[]>([]);
    const [isResending, setIsResending] = useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
    const [showEmailWarning, setShowEmailWarning] = useState<boolean>(false);

    useEffect(() => {
        loadThirdPartyMessages();
    }, []);

    return (
        <Modal
            actionButtons={[
                <Button
                    onClick={props.onClose}
                    variant="outline"
                >
                    {translateKey('common.cancel')}
                </Button>,
                <Button
                    isDisabled={isResending || checkedThirdPartyMessages.length === 0 || showEmailWarning}
                    onClick={async () => await resendThirdPartyMessages()}
                >
                    {translateKey('processDetails.resend')}
                </Button>,
            ]}
            isOpen
            onClose={props.onClose}
            size="large"
            title={translateKey('processDetails.resendThirdPartyMessagesTitle')}
        >
            <AdminTable>
                <TableHead>
                    <TableRow>
                        <TableHeader></TableHeader>
                        <TableHeader>{translateKey('processThirdPartyMessages.messageType')}</TableHeader>
                        <TableHeader>{translateKey('processThirdPartyMessages.messageSubtype')}</TableHeader>
                        <TableHeader>{translateKey('processThirdPartyMessages.recipient')}</TableHeader>
                        <TableHeader>{translateKey('common.status')}</TableHeader>
                        <TableHeader>{translateKey('processThirdPartyMessages.firstScheduledDateTime')}</TableHeader>
                        <TableHeader>{translateKey('processThirdPartyMessages.finishedDateTime')}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {thirdPartyMessages.map((thirdPartyMessage, i) => (
                        <TableRow key={'thirdPartyMessage' + i}>
                            <TableData>
                                <CheckboxInput
                                    checked={checkedThirdPartyMessages.includes(thirdPartyMessage.id)}
                                    onChange={(isChecked) => {
                                        if (isChecked) {
                                            setCheckedThirdPartyMessages([
                                                ...checkedThirdPartyMessages,
                                                thirdPartyMessage.id,
                                            ]);
                                        } else {
                                            setCheckedThirdPartyMessages(
                                                [...checkedThirdPartyMessages].filter(
                                                    (item) => item !== thirdPartyMessage.id
                                                )
                                            );
                                        }
                                    }}
                                />
                            </TableData>
                            <TableData>{thirdPartyMessage.messageType}</TableData>
                            <TableData>{thirdPartyMessage.messageSubtype}</TableData>
                            <TableData>{thirdPartyMessage.recipient}</TableData>
                            <TableData>
                                {translateKey('processThirdPartyMessages.' + thirdPartyMessage.status)}
                            </TableData>
                            <TableData>
                                <AdminProcessNullableFormattedDatetime
                                    value={thirdPartyMessage.firstScheduledDateTime}
                                />
                            </TableData>
                            <TableData>
                                <AdminProcessNullableFormattedDatetime value={thirdPartyMessage.finishedDateTime} />
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </AdminTable>

            {showSuccessMessage && <Alert appearance="info">{translateKey('processDetails.resendSuccess')}</Alert>}

            {showEmailWarning && <Alert appearance="warning">{translateKey('processDetails.emailWarning')}</Alert>}
        </Modal>
    );

    async function loadThirdPartyMessages() {
        const result = await abortableFetch.fetchJson(
            `/api/requests/${props.adminProcess.id}/resendableThirdPartyMessages`,
            'GET'
        );
        const resendableMessages: IThirdPartyMessage[] = result.json.items;
        setThirdPartyMessages(resendableMessages);
    }

    async function resendThirdPartyMessages() {
        setIsResending(true);
        setShowSuccessMessage(false);

        const patientMustHaveEmail = thirdPartyMessages.some(
            (message) =>
                checkedThirdPartyMessages.includes(message.id) &&
                message.messageType === ThirdPartyMessageType.ectosenseSendInvitation
        );
        if (patientMustHaveEmail && !props.adminProcess.patient.emailAddress) {
            setShowEmailWarning(true);
        } else {
            await abortableFetch.fetchJson('/api/thirdPartyMessages/resend', 'POST', checkedThirdPartyMessages);
            setShowSuccessMessage(true);
        }

        setIsResending(false);
    }
}
